import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Breadcrumb, SimpleCard } from "app/components";
import { GETALLUSERS } from "app/redux/actions/users";
import { createOvertimeRequest } from "app/redux/actions/overtimeActions";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const CreateOvertimeRequest = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { allUsers } = useSelector((state) => state.user);
  const [formData, setFormData] = useState({
    userId: "",
    date: dayjs(),
    hours: "",
    reason: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(GETALLUSERS({ page: 1, limit: 1000 }));
  }, [dispatch]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (newDate) => {
    setFormData((prev) => ({ ...prev, date: newDate }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !formData.userId ||
      !formData.date ||
      !formData.hours ||
      !formData.reason
    ) {
      toast.warn("Please fill all required fields.");
      return;
    }
    if (isNaN(formData.hours) || parseFloat(formData.hours) <= 0) {
      toast.error("Hours must be a positive number.");
      return;
    }

    setLoading(true);
    const requestData = {
      ...formData,
      date: formData.date.format("YYYY-MM-DD"),
      hours: parseFloat(formData.hours),
    };

    try {
      await dispatch(createOvertimeRequest(requestData));
      navigate("/all-overtime-requests");
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <SimpleCard>
      <Breadcrumb
        routeSegments={[
          { name: "All Overtime Requests", path: "/all-overtime-requests" },
          { name: "Create Request" },
        ]}
      />
      <Typography variant="h5" sx={{ mb: 3 }}>
        Create Manual Overtime Request
      </Typography>
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        <FormControl fullWidth required>
          <InputLabel>Select Employee</InputLabel>
          <Select
            name="userId"
            value={formData.userId}
            onChange={handleChange}
            label="Select Employee"
          >
            {(allUsers?.users || []).map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Date"
            value={formData.date}
            onChange={handleDateChange}
            renderInput={(params) => (
              <TextField {...params} fullWidth required />
            )}
          />
        </LocalizationProvider>

        <TextField
          name="hours"
          label="Overtime Hours"
          type="number"
          value={formData.hours}
          onChange={handleChange}
          fullWidth
          required
          inputProps={{ step: "0.01", min: "0" }}
        />

        <TextField
          name="reason"
          label="Reason"
          value={formData.reason}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          required
        />

        <Box
          sx={{ display: "flex", justifyContent: "flex-start", gap: 2, mt: 2 }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Create Request"}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            disabled={loading}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </SimpleCard>
  );
};

export default CreateOvertimeRequest;
